// Picking one of the person's people (the "who" of a waiting): a pill that
// opens a search over names and aliases, with "create a person named …" at the
// end. A created person is picked once the server has made them (until then
// the pick has no id and cannot be sent).
import { useQuery } from "@tanstack/react-query";
import {
  PERSON_NAME_MAX,
  type PersonSummary,
} from "@trameria/core/persons-shared";
import { m } from "@trameria/i18n";
import {
  type Dispatch,
  type KeyboardEvent,
  type RefObject,
  type SetStateAction,
  useId,
  useRef,
  useState,
} from "react";
import { useApi } from "../app/context";
import { apiErrorMessage } from "../data/errors";
import { isOptimisticId } from "../data/optimistic";
import { Avatar } from "../ui/bits";
import { Icon } from "../ui/icon";
import { isImeComposing } from "../ui/ime";
import { Popover } from "../ui/popover";
import { personsQuery, useCreatePerson } from "./queries";

export interface PickedPerson {
  /** null while the person is being created. */
  id: string | null;
  name: string;
}

/** People whose name or an alias holds the words (any case). */
export function matchPersons(
  persons: readonly PersonSummary[],
  query: string,
): PersonSummary[] {
  const q = query.trim().toLowerCase();
  return persons.filter(
    (p) =>
      !isOptimisticId(p.id) &&
      (q === "" ||
        p.name.toLowerCase().includes(q) ||
        p.aliases.some((a) => a.toLowerCase().includes(q))),
  );
}

export const PILL =
  "inline-flex h-[26px] max-w-full cursor-pointer items-center gap-[6px] rounded-[13px] border border-border px-[10px] text-[12px] text-body hover:border-field-strong";

export function PersonPill({
  value,
  onChange,
  onError,
}: {
  value: PickedPerson | null;
  onChange: Dispatch<SetStateAction<PickedPerson | null>>;
  onError(message: string | null): void;
}) {
  const anchor = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const create = useCreatePerson();
  const createNamed = (name: string) => {
    onError(null);
    onChange({ id: null, name });
    const still = (cur: PickedPerson | null) =>
      cur !== null && cur.id === null && cur.name === name;
    create.mutate(
      { name },
      {
        onSuccess: (p) =>
          onChange((cur) => (still(cur) ? { id: p.id, name: p.name } : cur)),
        onError: (err) => {
          onChange((cur) => (still(cur) ? null : cur));
          onError(apiErrorMessage(err, m.persons_create_failed()));
        },
      },
    );
  };
  return (
    <>
      <button
        ref={anchor}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={m.persons_pick_action()}
        onClick={() => setOpen((v) => !v)}
        className={`${PILL} ${value ? "" : "text-muted"}`}
      >
        {value ? (
          <Avatar name={value.name} size={16} />
        ) : (
          <Icon kind="person" size={13} />
        )}
        <span className="min-w-0 truncate">
          {value ? value.name : m.persons_pick_placeholder()}
        </span>
        {value?.id === null && (
          <span className="text-faint">{m.persons_creating()}</span>
        )}
      </button>
      {open && (
        <PersonPickPopover
          anchor={anchor}
          selectedId={value?.id ?? null}
          onClose={() => setOpen(false)}
          onPick={(p) => {
            setOpen(false);
            onError(null);
            onChange(p ? { id: p.id, name: p.name } : null);
          }}
          onCreate={(name) => {
            setOpen(false);
            createNamed(name);
          }}
        />
      )}
    </>
  );
}

type Row =
  | { kind: "person"; person: PersonSummary }
  | { kind: "create"; name: string }
  | { kind: "clear" };

export function PersonPickPopover({
  anchor,
  selectedId,
  exclude,
  onClose,
  onPick,
  onCreate,
  label = m.persons_pick_search(),
}: {
  anchor: RefObject<HTMLElement | null>;
  selectedId: string | null;
  /** Never offered (the person themself, when merging). */
  exclude?: string;
  onClose(): void;
  /** null = take the pick away. */
  onPick(person: PersonSummary | null): void;
  /** Without it, no "create" row is offered. */
  onCreate?(name: string): void;
  label?: string;
}) {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(0);
  const list = useQuery(personsQuery(useApi(), null));
  const q = query.trim();
  const persons = matchPersons(list.data?.items ?? [], q).filter(
    (p) => p.id !== exclude,
  );
  const exact = persons.some((p) => p.name.toLowerCase() === q.toLowerCase());
  const rows: Row[] = [
    ...(selectedId !== null && q === "" ? [{ kind: "clear" as const }] : []),
    ...persons
      .slice(0, 50)
      .map((person) => ({ kind: "person" as const, person })),
    ...(onCreate && q !== "" && !exact
      ? [{ kind: "create" as const, name: q.slice(0, PERSON_NAME_MAX) }]
      : []),
  ];
  const listId = useId();
  const active = Math.min(selected, rows.length - 1);
  const optionId = (i: number) => `${listId}-${i}`;
  const choose = (row: Row) => {
    if (row.kind === "person") onPick(row.person);
    else if (row.kind === "create") onCreate?.(row.name);
    else onPick(null);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (isImeComposing(e.nativeEvent)) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (rows.length === 0) return;
      const step = e.key === "ArrowDown" ? 1 : -1;
      setSelected((Math.max(active, 0) + step + rows.length) % rows.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const row = rows[active];
      if (row) choose(row);
    }
  };
  return (
    <Popover
      anchor={anchor}
      open
      onClose={onClose}
      label={label}
      className="w-[300px] max-w-[calc(100vw-16px)]"
    >
      <div className="px-2 pt-1 pb-1.5">
        <input
          type="text"
          role="combobox"
          aria-label={label}
          aria-expanded="true"
          aria-controls={listId}
          aria-activedescendant={active >= 0 ? optionId(active) : undefined}
          aria-autocomplete="list"
          placeholder={
            onCreate
              ? m.persons_pick_search_or_create()
              : m.persons_pick_search_placeholder()
          }
          value={query}
          maxLength={PERSON_NAME_MAX}
          onChange={(e) => {
            setQuery(e.target.value);
            setSelected(0);
          }}
          onKeyDown={onKeyDown}
          // biome-ignore lint/a11y/noAutofocus: opened to search
          autoFocus
          className="plain-field w-full rounded-[6px] border border-field bg-surface px-2 py-[5px] text-[12.5px] text-body outline-none focus:border-field-strong"
        />
      </div>
      <div id={listId} role="listbox" aria-label={m.persons_pick_candidates()}>
        {rows.length === 0 && !list.isPending && (
          <p className="px-3 py-2 text-[12px] text-faint">
            {m.persons_pick_none()}
          </p>
        )}
        {rows.map((row, i) => (
          <button
            key={row.kind === "person" ? row.person.id : row.kind}
            id={optionId(i)}
            type="button"
            role="option"
            tabIndex={-1}
            aria-selected={i === active}
            onMouseDown={(e) => {
              e.preventDefault();
              choose(row);
            }}
            onMouseEnter={() => setSelected(i)}
            className={`flex h-8 w-full cursor-pointer items-center gap-2 px-3 text-left text-[13px] ${
              i === active ? "bg-hover" : ""
            } ${row.kind === "clear" ? "text-faint" : "text-body"}`}
          >
            {row.kind === "person" && (
              <>
                <Avatar name={row.person.name} size={16} />
                <span className="min-w-0 shrink truncate">
                  {row.person.name}
                </span>
                {row.person.summary && (
                  <span className="min-w-0 flex-1 basis-0 truncate text-[11.5px] text-muted">
                    {row.person.summary}
                  </span>
                )}
                {row.person.id === selectedId && (
                  <Icon
                    kind="check"
                    size={14}
                    className="ml-auto text-accent"
                  />
                )}
              </>
            )}
            {row.kind === "create" && (
              <>
                <Icon kind="plus" size={14} className="text-muted" />
                <span className="min-w-0 truncate">
                  {m.persons_create_named({ name: row.name })}
                </span>
              </>
            )}
            {row.kind === "clear" && <span>{m.persons_pick_clear()}</span>}
          </button>
        ))}
      </div>
    </Popover>
  );
}
